// =============================================================================
// Origin UI — src/pages/employees/EmployeeListPage.tsx
// Employee directory with search, filters, and role-aware data scoping.
// HR Admin sees all employees. Managers see direct reports.
// Phase 4 — Employee Records module.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/apiService';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner, EmptyState } from '@/components/common';
import styles from './employees.module.css';

interface EmployeeListItem {
  employeeId: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  preferredFirstName: string | null;
  preferredLastName: string | null;
  displayName: string | null;
  vistaEmployeeId: string | null;
  email: string;
  phone: string | null;
  profilePhotoUrl: string | null;
  jobTitle: string;
  departmentName: string;
  officeName: string;
  employmentType: string;
  employmentStatus: string;
  joinDate: string;
  managerId: string | null;
  managerName: string | null;
}

type SortKey = 'name' | 'jobTitle' | 'departmentName' | 'officeName' | 'employmentType' | 'employmentStatus' | 'joinDate';

/** Returns true if the employee's join date is in the future (pre-starter). */
function isPreStarter(emp: EmployeeListItem): boolean {
  if (!emp.joinDate) return false;
  const join = new Date(emp.joinDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  join.setHours(0, 0, 0, 0);
  return join > today;
}

export function EmployeeListPage() {
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { roles } = useAuth();
  const navigate = useNavigate();

  // Reference data for filters
  const [departments, setDepartments] = useState<string[]>([]);
  const [offices, setOffices] = useState<string[]>([]);

  useEffect(() => { loadEmployees(); }, []);

  async function loadEmployees() {
    try {
      setLoading(true);
      setError('');
      const data = await api.get<EmployeeListItem[]>('/api/employees');
      setEmployees(data);

      // Extract unique departments and offices for filters
      const depts = [...new Set(data.map(e => e.departmentName))].sort();
      const offs = [...new Set(data.map(e => e.officeName))].sort();
      setDepartments(depts);
      setOffices(offs);
    } catch (err: any) {
      setError(err.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }

  // Client-side filtering (API already scopes by role)
  const filtered = employees.filter(e => {
    if (deptFilter && e.departmentName !== deptFilter) return false;
    if (officeFilter && e.officeName !== officeFilter) return false;
    if (statusFilter && e.employmentStatus !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      const displayName = getDisplayName(e);
      return (
        displayName.toLowerCase().includes(s) ||
        e.firstName.toLowerCase().includes(s) ||
        e.lastName.toLowerCase().includes(s) ||
        e.email.toLowerCase().includes(s) ||
        e.employeeNumber.toLowerCase().includes(s) ||
        (e.vistaEmployeeId || '').toLowerCase().includes(s) ||
        e.jobTitle.toLowerCase().includes(s) ||
        e.departmentName.toLowerCase().includes(s)
      );
    }
    return true;
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return null;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === 'name') {
      av = getDisplayName(a).toLowerCase();
      bv = getDisplayName(b).toLowerCase();
    } else if (sortKey === 'joinDate') {
      av = new Date(a.joinDate).getTime();
      bv = new Date(b.joinDate).getTime();
    } else {
      av = (a[sortKey] || '').toString().toLowerCase();
      bv = (b[sortKey] || '').toString().toLowerCase();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Status counts
  const statusCounts: Record<string, number> = {};
  employees.forEach(e => {
    statusCounts[e.employmentStatus] = (statusCounts[e.employmentStatus] || 0) + 1;
  });

  function getInitials(first: string, last: string): string {
    return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase();
  }

  function getDisplayName(e: EmployeeListItem): string {
    if (e.displayName) return e.displayName;
    const first = e.preferredFirstName || e.preferredName || e.firstName;
    const last = e.preferredLastName || e.lastName;
    return first + " " + last;
  }

  function handleExportCSV() {
    const headers = ['Employee Number', 'First Name', 'Last Name', 'Preferred Name', 'Email', 'Job Title', 'Department', 'Office', 'Employment Type', 'Status', 'Join Date', 'Manager'];
    const rows = filtered.map(e => [
      e.employeeNumber,
      e.firstName,
      e.lastName,
      e.preferredName || '',
      e.email,
      e.jobTitle,
      e.departmentName,
      e.officeName,
      e.employmentType,
      e.employmentStatus,
      new Date(e.joinDate).toLocaleDateString('en-GB'),
      e.managerName || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell ?? '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleFullExport() {
    try {
      setError('');
      const token = await api.getToken();

      // Raw fetch must be prefixed with the API base URL. Without it the
      // request resolves against the UI host and the SPA fallback returns
      // index.html (which a 200 status hides). Mirror apiService's base URL.
      const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
      const response = await fetch(`${base}/api/employees/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Export failed (HTTP ${response.status})`);
      }

      // Guard against a misrouted request silently returning HTML.
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('csv')) {
        throw new Error('Export returned unexpected content — check the API base URL configuration.');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_full_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export');
    }
  }

  if (loading) return <LoadingSpinner message="Loading employees..." />;

  return (
    <div>
      <PageHeader
        title="Employees"
        subtitle={`${employees.length} employee${employees.length !== 1 ? 's' : ''}`}
        action={
          roles.isHRAdmin ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className={styles.filterSelect} onClick={handleExportCSV} style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                ↓ Quick CSV
              </button>
              <button className={styles.filterSelect} onClick={handleFullExport} style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                ↓ Full Export
              </button>
              <button className={styles.createBtn} onClick={() => navigate('/requests/new')}>
                + New Onboarding
              </button>
            </div>
          ) : undefined
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Search and filters */}
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search by name, email, OQT ID, job title..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={styles.filterSelect}
          value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)}
        >
          <option value="">All departments</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          className={styles.filterSelect}
          value={officeFilter}
          onChange={e => setOfficeFilter(e.target.value)}
        >
          <option value="">All offices</option>
          {offices.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {/* Status pills */}
      <div className={styles.statusPills}>
        <button
          className={`${styles.pill} ${!statusFilter ? styles.pillActive : ''}`}
          onClick={() => setStatusFilter('')}
        >
          All ({employees.length})
        </button>
        {Object.entries(statusCounts).sort().map(([status, count]) => (
          <button
            key={status}
            className={`${styles.pill} ${statusFilter === status ? styles.pillActive : ''}`}
            onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
          >
            <span className={`${styles.statusDot} ${styles['dot' + status]}`} />
            {status} ({count})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No employees found"
          message={search || deptFilter || officeFilter || statusFilter
            ? 'Try adjusting your search or filters.'
            : 'No employee records yet.'}
        />
      ) : (
        <Card padding={false}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Employee{sortIndicator('name')}</th>
                  <th onClick={() => toggleSort('jobTitle')} style={{ cursor: 'pointer', userSelect: 'none' }}>Job Title{sortIndicator('jobTitle')}</th>
                  <th onClick={() => toggleSort('departmentName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Department{sortIndicator('departmentName')}</th>
                  <th onClick={() => toggleSort('officeName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Office{sortIndicator('officeName')}</th>
                  <th onClick={() => toggleSort('employmentType')} style={{ cursor: 'pointer', userSelect: 'none' }}>Type{sortIndicator('employmentType')}</th>
                  <th onClick={() => toggleSort('employmentStatus')} style={{ cursor: 'pointer', userSelect: 'none' }}>Status{sortIndicator('employmentStatus')}</th>
                  <th onClick={() => toggleSort('joinDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Joined{sortIndicator('joinDate')}</th>
                  {roles.isHRAdmin && <th>OQT ID</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map(emp => {
                  const preStarter = isPreStarter(emp);
                  return (
                    <tr
                      key={emp.employeeId}
                      className={styles.tableRow}
                      onClick={() => navigate(`/employees/${emp.employeeId}`)}
                    >
                      <td>
                        <div className={styles.empCell}>
                          <div className={styles.avatar}>
                            {emp.profilePhotoUrl
                              ? <img src={emp.profilePhotoUrl} alt="" className={styles.avatarImg} />
                              : getInitials(emp.firstName, emp.lastName)
                            }
                          </div>
                          <div className={styles.empInfo}>
                            <div className={styles.empName}>{getDisplayName(emp)}</div>
                            <div className={styles.empMeta}>{emp.employeeNumber} · {emp.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className={styles.cellTitle}>{emp.jobTitle}</td>
                      <td>{emp.departmentName}</td>
                      <td>{emp.officeName}</td>
                      <td>
                        <span className={`${styles.typeBadge} ${emp.employmentType === 'Contract' ? styles.typeContract : ''}`}>
                          {emp.employmentType}
                        </span>
                      </td>
                      <td>
                        {preStarter ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              fontSize: '12px',
                              fontWeight: 600,
                              borderRadius: '4px',
                              backgroundColor: '#EEF2FF',
                              color: '#4338CA',
                              border: '1px solid #C7D2FE',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Starts {new Date(emp.joinDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                        ) : (
                          <span className={`${styles.statusBadge} ${styles['status' + emp.employmentStatus]}`}>
                            {emp.employmentStatus}
                          </span>
                        )}
                      </td>
                      <td className={styles.cellDate}>
                        {new Date(emp.joinDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      {roles.isHRAdmin && (
                        <td className={styles.cellDate}>{emp.vistaEmployeeId || '—'}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className={styles.resultCount}>
        Showing {filtered.length} of {employees.length} employees
      </div>
    </div>
  );
}
